<template>
  <Dialog
    ref="dialog"
    class="transaction-details-dialog"
    dismissible
  >
    <div class="dialog-body">
      <h4 class="text-left">
        {{ $gettext('Payout ID:') }}
        <span class="text-blue-alternate">
          &nbsp;#{{ transactionId }}
        </span>
      </h4>

      <Notification
        v-if="error"
        icon-name="danger"
        :title="$gettext('Oops! Something’s not right here.')"
        :message="$gettext('Please check your internet connection and reload the page.')"
      />

      <Loader v-else-if="isLoading" />

      <div
        v-else-if="transaction"
        class="text-left"
      >
        <div class="row no-space">
          <div class="col-6">
            <p class="amount">
              <strong v-translate>Payout</strong>
              <br>
              <span>
                {{
                  ( transaction.requestedAmount && transaction.requestedCurrency )
                    ? formatPrice(transaction.requestedAmount, transaction.requestedCurrency)
                    : formatPrice(transaction.amount, 'GBP')
                }}
              </span>
            </p>

            <p
              v-if="displayPayoutInGbp"
              class="payout-gbp"
            >
              <strong v-translate>Payout in GBP</strong>
              <br>
              <span>
                {{ `${formatPrice(transaction.amount, 'GBP')} GBP` }}
              </span>
            </p>

            <p class="currency">
              <strong v-translate>Amount Received</strong>
              <br>
              <span>{{ transactionStatusPaid ? amountReceived : '-' }}</span>
            </p>
            <p class="exchange-rate">
              <strong v-translate>Exchange rate</strong>
              <br>
              <span>{{ exchangeRate }}</span>
            </p>
            <p class="payout-type">
              <strong v-translate>Payout type</strong>
              <br>
              <span>{{ transaction.type }}</span>
            </p>
          </div>
          <div class="col-6">
            <p class="account">
              <strong v-translate>Payout account</strong>
              <br>
              <span>{{ payoutAccount }}</span>
            </p>
            <p class="requested-on">
              <strong v-translate>Requested on</strong>
              <br>
              <span>{{ formatDate(transaction.createdAt) }}</span>
            </p>
            <p class="paid-on">
              <strong v-translate>Paid on</strong>
              <br>
              <span v-if="transaction.paidOn">{{ formatDate(transaction.paidOn) }}</span>
              <span v-else>-</span>
            </p>
            <p class="status">
              <strong v-translate>Status</strong>
              <br>
              <strong :class="getStatusClass(transaction.status)">
                {{ $gettext(getStatusText(transaction.status)) }}
              </strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { Action } from 'vuex-class'
import Dialog from '@/components/CommonUI/Dialog.vue'
import Icon from '@/components/CommonUI/Icon.vue'
import Loader from '@/components/CommonUI/Loader.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import transactionStatuses from '@/mixins/transactionStatuses'
import { TransactionStatus, currencySymbol, supportedCurrencyConversions } from '@/constants'
import { getErrorMessage } from '@/helpers/errorHandler'
import { formatAmount } from '@/helpers/formatAmount'
import { formatDate } from '@/helpers/formatDate'
import { formatPrice } from '@/helpers/formatPrice'

@Options({
  components: {
    Dialog,
    Icon,
    Loader,
    Notification,
  },
  mixins: [
    transactionStatuses,
  ],
})

export default class TransactionDetailsDialog extends Vue {
  transactionId = 0
  transaction: Transaction | null = null
  error: string | null = null
  isLoading = true

  public TransactionStatus = TransactionStatus

  $refs!: {
    dialog: InstanceType<typeof Dialog>,
  }

  @Action getTransactionDetails!: (id: number) => Promise<Transaction>

  getStatusClass!: (status: TransactionStatus) => string
  getStatusText!: (status: TransactionStatus) => string

  formatAmount = formatAmount
  formatDate = formatDate
  formatPrice = formatPrice

  get payoutAccount(): string | null {
    const payoutAccountInformation = this.transaction?.typeAttributes?.accountInformation
    if (payoutAccountInformation && payoutAccountInformation.length > 12) {
      return `**********${payoutAccountInformation.slice(-2)}`
    }
    return payoutAccountInformation?.length
      ? payoutAccountInformation
      : '-'
  }

  get targetCurrency(): keyof typeof currencySymbol | null {
    return this.transaction?.typeAttributes?.targetCurrency || null
  }

  get targetCurrencySymbol(): string | null {
    return this.targetCurrency
      ? currencySymbol[this.targetCurrency]
      : null
  }

  get currencyReceived(): string | null | undefined {
    return this.transaction?.typeAttributes?.currencyReceived
  }

  get amountReceived(): string {
    const amountReceived = this.transaction?.typeAttributes?.amountReceived

    return amountReceived && this.currencyReceived
      ? `${formatPrice(amountReceived, this.currencyReceived)} ${this.currencyReceived}`
      : `${formatPrice(this.transaction?.amount, this.transaction?.currency)} ${this.transaction?.currency}`
  }

  get displayPayoutInGbp(): boolean {
    return this.transactionStatusPaid && Boolean(this.currencyReceived && this.currencyReceived !== 'GBP')
  }

  get transactionStatusPaid(): boolean {
    return this.transaction?.status === TransactionStatus.paid
  }

  get exchangeRate(): string {
    const exchangeRate = this.transaction?.typeAttributes?.exchangeRate

    return exchangeRate
      ? `£1 GBP = ${this.targetCurrencySymbol}${Number(exchangeRate).toFixed(4)} ${this.targetCurrency}`
      : '-'
  }

  hide() {
    this.$refs.dialog.hide()
    this.transaction = null
  }

  show(transactionId: number) {
    this.transactionId = transactionId
    this.getDetails(transactionId)
    this.$refs.dialog.show()
  }

  async getDetails(transactionId: number) {
    try {
      this.isLoading = true
      this.transaction = await this.getTransactionDetails(transactionId)
      this.isLoading = false
    } catch (error) {
      this.error = getErrorMessage(error)
    }
  }
}
</script>
